import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, RefreshCw, CheckCircle2, Clock, Landmark, CircleDollarSign, Undo2, StickyNote, PackagePlus, ChevronDown, Send, Package } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";

const TOKEN_KEY = "ap_admin_token";

const fmtError = (detail) => {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [trackDraft, setTrackDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [products, setProducts] = useState([]);
  const [stockDrafts, setStockDrafts] = useState({});

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async (t = token) => {
    try {
      const r = await api.get("/admin/orders", { headers: { Authorization: `Bearer ${t}` } });
      setOrders(r.data);
      const pr = await api.get("/products");
      setProducts(pr.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setOrders(null);
      } else {
        toast.error("Could not load orders.");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      setPassword("");
    } catch (e2) {
      setError(fmtError(e2.response?.data?.detail));
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setOrders(null);
  };

  const patchOrder = (orderId, patch) =>
    setOrders((prev) => prev.map((o) => (o.order_id === orderId ? { ...o, ...patch } : o)));

  const setStatus = async (orderId, action) => {
    setBusyId(orderId);
    try {
      const r = await api.post(`/admin/orders/${orderId}/${action}`, {}, auth());
      patchOrder(orderId, { status: r.data.status });
      if (action === "mark-paid") {
        toast.success(r.data.customer_notified ? `${orderId} marked paid - customer emailed` : `${orderId} marked paid (email failed)`);
      } else {
        toast.success(`${orderId} moved back to awaiting payment`);
      }
    } catch (e) {
      if (e.response?.status === 401) return logout();
      toast.error(fmtError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const toggleExpand = (o) => {
    if (expandedId === o.order_id) return setExpandedId(null);
    setExpandedId(o.order_id);
    setTrackDraft(o.tracking_number || "");
    setNoteDraft(o.note || "");
  };

  const saveNote = async (orderId) => {
    setBusyId(orderId);
    try {
      await api.post(`/admin/orders/${orderId}/note`, { note: noteDraft }, auth());
      patchOrder(orderId, { note: noteDraft });
      toast.success(`Note saved on ${orderId}`);
    } catch (e) {
      if (e.response?.status === 401) return logout();
      toast.error(fmtError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const sendTracking = async (orderId) => {
    if (!trackDraft.trim()) return toast.error("Enter a tracking number first.");
    setBusyId(orderId);
    try {
      const r = await api.post(`/admin/orders/${orderId}/tracking`, { tracking_number: trackDraft }, auth());
      patchOrder(orderId, { tracking_number: trackDraft, shipped_at: new Date().toISOString() });
      toast.success(r.data.customer_notified ? `Shipping email sent to customer` : `Tracking saved (email failed)`);
    } catch (e) {
      if (e.response?.status === 401) return logout();
      toast.error(fmtError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const saveStock = async (pid) => {
    const val = parseInt(stockDrafts[pid], 10);
    if (isNaN(val) || val < 0) return toast.error("Enter a valid stock count.");
    setBusyId(`stock-${pid}`);
    try {
      const r = await api.post(`/admin/products/${pid}/stock`, { stock: val }, auth());
      setProducts((prev) => prev.map((p) => (p.id === pid ? { ...p, stock: r.data.stock, in_stock: r.data.in_stock } : p)));
      setStockDrafts((prev) => ({ ...prev, [pid]: undefined }));
      toast.success("Stock updated");
    } catch (e) {
      if (e.response?.status === 401) return logout();
      toast.error(fmtError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5" data-testid="admin-login-page">
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          onSubmit={login}
          className="glass w-full max-w-sm rounded-2xl p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-[#E8E9EB]" />
            <h1 className="font-display text-xl font-bold text-white">Admin Access</h1>
          </div>
          <p className="mt-2 text-xs text-slate-500">Order management for Alpha Peps.</p>
          <input
            type="password"
            required
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="mt-6 w-full rounded-lg border border-white/10 bg-[#141417] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors duration-200 focus:border-[#E8E9EB]/60"
          />
          {error && <p className="mt-3 text-xs text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-5 w-full rounded-full bg-[#6E7683] py-3 font-mono text-xs tracking-[0.25em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811]"
          >
            LOG IN
          </button>
        </motion.form>
      </div>
    );
  }

  const awaiting = orders?.filter((o) => o.status === "awaiting_payment") || [];
  const paid = orders?.filter((o) => o.status === "paid") || [];
  const revenue = paid.reduce((s, o) => s + o.total, 0);
  const visible = orders?.filter((o) => (filter === "all" ? true : filter === "awaiting" ? o.status === "awaiting_payment" : o.status === "paid")) || [];

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24 pt-28 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">ADMIN</div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-white" data-testid="admin-heading">Orders</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={() => load()} data-testid="admin-refresh-button" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono text-[11px] tracking-[0.15em] text-slate-300 transition-colors duration-200 hover:border-[#E8E9EB]/50 hover:text-white">
            <RefreshCw className="h-3.5 w-3.5" /> REFRESH
          </button>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono text-[11px] tracking-[0.15em] text-slate-300 transition-colors duration-200 hover:border-red-400/50 hover:text-red-300">
            <LogOut className="h-3.5 w-3.5" /> LOG OUT
          </button>
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <div className="glass rounded-xl p-5" data-testid="stat-awaiting">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-slate-500"><Clock className="h-3.5 w-3.5" /> AWAITING PAYMENT</div>
          <div className="mt-2 font-display text-3xl font-bold text-amber-400">{awaiting.length}</div>
        </div>
        <div className="glass rounded-xl p-5" data-testid="stat-paid">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-slate-500"><CheckCircle2 className="h-3.5 w-3.5" /> PAID ORDERS</div>
          <div className="mt-2 font-display text-3xl font-bold text-emerald-400">{paid.length}</div>
        </div>
        <div className="glass rounded-xl p-5" data-testid="stat-revenue">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-slate-500"><CircleDollarSign className="h-3.5 w-3.5" /> REVENUE RECEIVED</div>
          <div className="mt-2 font-display text-3xl font-bold text-white">{money(revenue)}</div>
        </div>
      </div>

      <div className="glass mt-8 rounded-xl p-5" data-testid="inventory-section">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-slate-500">
          <Package className="h-3.5 w-3.5" /> INVENTORY - SET STOCK COUNTS
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {products.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 px-4 py-2.5" data-testid={`stock-row-${p.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm text-white">
                  {p.name} <span className="font-mono text-[10px] text-slate-500">{p.dose}</span>
                </div>
                <div className={`mt-0.5 font-mono text-[9px] tracking-[0.15em] ${(p.stock ?? 0) > 3 ? "text-emerald-400" : (p.stock ?? 0) > 0 ? "text-amber-400" : "text-red-400"}`} data-testid={`stock-status-${p.id}`}>
                  {(p.stock ?? 0) > 0 ? `${p.stock} IN STOCK` : "OUT OF STOCK"}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={stockDrafts[p.id] ?? p.stock ?? 0}
                  onChange={(e) => setStockDrafts({ ...stockDrafts, [p.id]: e.target.value })}
                  className="w-16 rounded-md border border-white/10 bg-[#141417] px-2 py-1.5 text-center font-mono text-xs text-white outline-none focus:border-[#E8E9EB]/60"
                  data-testid={`stock-input-${p.id}`}
                />
                <button
                  onClick={() => saveStock(p.id)}
                  disabled={busyId === `stock-${p.id}`}
                  data-testid={`stock-save-${p.id}`}
                  className="rounded-full border border-white/10 px-3 py-1.5 font-mono text-[9px] tracking-[0.15em] text-slate-300 transition-colors duration-200 hover:border-[#E8E9EB]/50 hover:text-white disabled:opacity-40"
                >
                  SAVE
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex gap-2" data-testid="order-filters">
        {[["all", "ALL"], ["awaiting", "AWAITING"], ["paid", "PAID"]].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`rounded-full px-4 py-2 font-mono text-[10px] tracking-[0.2em] transition-colors duration-200 ${
              filter === k ? "bg-[#E8E9EB] text-[#050811]" : "border border-white/10 text-slate-400 hover:border-[#E8E9EB]/40 hover:text-white"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-4" data-testid="orders-table">
        {orders === null ? (
          <p className="py-16 text-center font-mono text-xs tracking-[0.3em] text-slate-500">LOADING ORDERS...</p>
        ) : visible.length === 0 ? (
          <p className="glass rounded-xl py-16 text-center text-sm text-slate-500" data-testid="orders-empty">No orders in this view.</p>
        ) : (
          visible.map((o) => (
            <div key={o.order_id} className="glass rounded-xl p-5" data-testid={`order-row-${o.order_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold text-white">{o.order_id}</span>
                    {o.status === "paid" ? (
                      <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[9px] tracking-[0.15em] text-emerald-400" data-testid={`status-${o.order_id}`}>PAID</span>
                    ) : (
                      <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 font-mono text-[9px] tracking-[0.15em] text-amber-400" data-testid={`status-${o.order_id}`}>AWAITING PAYMENT</span>
                    )}
                    {o.tracking_number && (
                      <span className="rounded-full border border-white/10 px-2.5 py-0.5 font-mono text-[9px] tracking-[0.15em] text-slate-400">SHIPPED</span>
                    )}
                  </div>
                  <div className="mt-2 text-sm text-slate-300">{o.customer.name} · {o.customer.email}</div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.1em] text-slate-500">
                    {o.items.map((i) => `${i.name} x${i.qty}`).join(" / ")} · {(o.shipping_method || "standard").toUpperCase()} · {new Date(o.created_at).toLocaleDateString("en-AU")}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <span className="font-mono text-lg text-[#E8E9EB]">{money(o.total)}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleExpand(o)}
                      data-testid={`expand-button-${o.order_id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono text-[10px] tracking-[0.15em] text-slate-300 transition-colors duration-200 hover:border-[#E8E9EB]/50 hover:text-white"
                    >
                      <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${expandedId === o.order_id ? "rotate-180" : ""}`} /> NOTES & TRACKING
                    </button>
                    {o.status === "paid" ? (
                      <button
                        onClick={() => setStatus(o.order_id, "mark-unpaid")}
                        disabled={busyId === o.order_id}
                        data-testid={`mark-unpaid-button-${o.order_id}`}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono text-[10px] tracking-[0.15em] text-slate-400 transition-colors duration-200 hover:border-amber-400/50 hover:text-amber-300 disabled:opacity-50"
                      >
                        <Undo2 className="h-3 w-3" /> UNDO PAID
                      </button>
                    ) : (
                      <button
                        onClick={() => setStatus(o.order_id, "mark-paid")}
                        disabled={busyId === o.order_id}
                        data-testid={`mark-paid-button-${o.order_id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-[#6E7683] px-4 py-2 font-mono text-[10px] tracking-[0.15em] text-white transition-colors duration-200 hover:bg-emerald-500 hover:text-[#050811] disabled:opacity-50"
                      >
                        <Landmark className="h-3 w-3" /> {busyId === o.order_id ? "UPDATING..." : "MARK AS PAID"}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {expandedId === o.order_id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.3 }}
                  className="mt-5 grid gap-5 overflow-hidden border-t border-white/10 pt-5 sm:grid-cols-2"
                  data-testid={`order-detail-${o.order_id}`}
                >
                  <div className="rounded-xl border border-white/10 bg-[#141417] p-4">
                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-[#E8E9EB]">
                      <PackagePlus className="h-3.5 w-3.5" /> TRACKING NUMBER
                    </div>
                    {o.tracking_number && (
                      <p className="mt-2 font-mono text-[10px] text-emerald-400">SENT: {o.tracking_number}</p>
                    )}
                    <input
                      placeholder="e.g. 33TEZ7001234"
                      value={trackDraft}
                      onChange={(e) => setTrackDraft(e.target.value)}
                      data-testid={`tracking-input-${o.order_id}`}
                      className="mt-3 w-full rounded-lg border border-white/10 bg-[#0C0C0F] px-3 py-2.5 text-sm text-white placeholder-slate-600 outline-none transition-colors duration-200 focus:border-[#E8E9EB]/60"
                    />
                    <button
                      onClick={() => sendTracking(o.order_id)}
                      disabled={busyId === o.order_id}
                      data-testid={`send-tracking-button-${o.order_id}`}
                      className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#6E7683] px-4 py-2 font-mono text-[10px] tracking-[0.15em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811] disabled:opacity-50"
                    >
                      <Send className="h-3 w-3" /> EMAIL TRACKING TO CUSTOMER
                    </button>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-[#141417] p-4">
                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-[#E8E9EB]">
                      <StickyNote className="h-3.5 w-3.5" /> INTERNAL NOTE
                    </div>
                    <textarea
                      rows={3}
                      placeholder="Packing notes, payment reference, anything..."
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      data-testid={`note-input-${o.order_id}`}
                      className="mt-3 w-full rounded-lg border border-white/10 bg-[#0C0C0F] px-3 py-2.5 text-sm text-white placeholder-slate-600 outline-none transition-colors duration-200 focus:border-[#E8E9EB]/60"
                    />
                    <button
                      onClick={() => saveNote(o.order_id)}
                      disabled={busyId === o.order_id}
                      data-testid={`save-note-button-${o.order_id}`}
                      className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] tracking-[0.15em] text-slate-300 transition-colors duration-200 hover:border-[#E8E9EB]/50 hover:text-white disabled:opacity-50"
                    >
                      SAVE NOTE
                    </button>
                    <p className="mt-2 font-mono text-[9px] tracking-[0.1em] text-slate-600">NOTES ARE INTERNAL ONLY - NEVER EMAILED</p>
                  </div>
                </motion.div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
